import 
{ useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MapPin, Clock, LogOut, ChevronDown, Users, CheckCircle2, XCircle, Copy, RefreshCw, CalendarIcon, Eye, BarChart3, Settings, AlertTriangle, Trash2, CalendarOff, Shield, CalendarDays, FileSpreadsheet, Download, CreditCard, X, Bell } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { GroupScheduleTab } from "@/components/GroupScheduleTab";
import { ShiftsTab } from "@/components/ShiftsTab";
import { PayrollDialog } from "@/components/PayrollDialog";
import { TotalAnalyticsTab } from "@/components/TotalAnalyticsTab";
import { SecurityTab } from "@/components/SecurityTab";
import { AuditHistoryTab } from "@/components/AuditHistoryTab";
import { NotificationsTab } from "@/components/NotificationsTab";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmployerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, isRTL } = useLanguage();
  
  const [user, setUser] = useState(location.state?.user || null);
  const [company, setCompany] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [unassignedLocationEmployees, setUnassignedLocationEmployees] = useState([]);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState("month");
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [shiftsEnabled, setShiftsEnabled] = useState(true);
  const [companyName, setCompanyName] = useState("");
  const [companyPayType, setCompanyPayType] = useState("hourly");
  const [companyTimezone, setCompanyTimezone] = useState(3); // Default UTC+3
  const [employeeToRemove, setEmployeeToRemove] = useState(null);
  const [showPayrollDialog, setShowPayrollDialog] = useState(false);
  const [showScheduleExportDialog, setShowScheduleExportDialog] = useState(false);
  const [employeeViewMode, setEmployeeViewMode] = useState("live");
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem('employerBannerDismissed') === 'true');
  const [pendingScheduleEmployees, setPendingScheduleEmployees] = useState([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleEmployee, setScheduleEmployee] = useState(null);
  const [weeklySchedule, setWeeklySchedule] = useState([
    { day_of_week: 0, start_time: "09:00", end_time: "17:00", is_working_day: true },
    { day_of_week: 1, start_time: "09:00", end_time: "17:00", is_working_day: true },
    { day_of_week: 2, start_time: "09:00", end_time: "17:00", is_working_day: true },
    { day_of_week: 3, start_time: "09:00", end_time: "17:00", is_working_day: true },
    { day_of_week: 4, start_time: "09:00", end_time: "17:00", is_working_day: true },
    { day_of_week: 5, start_time: "09:00", end_time: "17:00", is_working_day: false },
    { day_of_week: 6, start_time: "09:00", end_time: "17:00", is_working_day: false },
  ]);
  const [lateNotificationsCount, setLateNotificationsCount] = useState(0);
  const [pendingShiftRequestsCount, setPendingShiftRequestsCount] = useState(0);
  const [showDeleteCompanyDialog, setShowDeleteCompanyDialog] = useState(false);
  const [showConfirmDeleteCompany, setShowConfirmDeleteCompany] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [showCancelSubscriptionDialog, setShowCancelSubscriptionDialog] = useState(false);
  
  // Timezone options for dropdown
  const TIMEZONE_OPTIONS = [
    { value: -12, label: "UTC-12 (Baker Island)" },
    { value: -11, label: "UTC-11 (Samoa)" },
    { value: -10, label: "UTC-10 (Hawaii)" },
    { value: -9, label: "UTC-9 (Alaska)" },
    { value: -8, label: "UTC-8 (Pacific Time)" },
    { value: -7, label: "UTC-7 (Mountain Time)" },
    { value: -6, label: "UTC-6 (Central Time)" },
    { value: -5, label: "UTC-5 (Eastern Time)" },
    { value: -4, label: "UTC-4 (Atlantic)" },
    { value: -3, label: "UTC-3 (Brazil)" },
    { value: -2, label: "UTC-2" },
    { value: -1, label: "UTC-1 (Azores)" },
    { value: 0, label: "UTC+0 (London)" },
    { value: 1, label: "UTC+1 (Paris)" },
    { value: 2, label: "UTC+2 (Cairo)" },
    { value: 3, label: "UTC+3 (Riyadh, Moscow)" },
    { value: 4, label: "UTC+4 (Dubai)" },
    { value: 5, label: "UTC+5 (Karachi)" },
    { value: 5.5, label: "UTC+5:30 (India)" },
    { value: 6, label: "UTC+6 (Dhaka)" },
    { value: 7, label: "UTC+7 (Bangkok)" },
    { value: 8, label: "UTC+8 (Singapore, Beijing)" },
    { value: 9, label: "UTC+9 (Tokyo)" },
    { value: 10, label: "UTC+10 (Sydney)" },
    { value: 11, label: "UTC+11 (Solomon Islands)" },
    { value: 12, label: "UTC+12 (Auckland)" },
  ];
  
  const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const dismissBanner = () => {
    setBannerDismissed(true);
    localStorage.setItem('employerBannerDismissed', 'true');
  };

  useEffect(() => {
    if (!user) {
      const fetchUser = async () => {
        try {
          const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
          if (res.ok) setUser(await res.json());
        } catch (e) { console.error(e); }
      };
      fetchUser();
    }
  }, [user]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(`${API_URL}/api/company`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setCompany(data);
          setShiftsEnabled(data.shifts_enabled !== false);
          setCompanyName(data.name || "");
          setCompanyPayType(data.pay_type || "hourly");
          setCompanyTimezone(data.timezone_offset ?? 3);
        }
      } catch (e) { console.error(e); }
    };
    
    const fetchSubscription = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscription/status`, { credentials: 'include' });
        if (res.ok) setSubscriptionStatus(await res.json());
      } catch (e) { console.error(e); }
    };
    
    const fetchPendingSchedules = async () => {
      try {
        const res = await fetch(`${API_URL}/api/employer/pending-schedules`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setPendingScheduleEmployees(data.pending_employees || []);
        }
      } catch (e) { console.error(e); }
    };
    
    fetchCompany();
    fetchSubscription();
    fetchPendingSchedules();
  }, []);

  const fetchEmployees = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/employees/status`, { credentials: 'include' });
      if (res.ok) setEmployees(await res.json());
    } catch (e) { console.error(e); }
    finally { setIsRefreshing(false); }
  }, []);

  const fetchUnassignedLocationEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/geofencing/unassigned-employees`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setUnassignedLocationEmployees(data.employees || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);
  
  const fetchTimeOffRequests = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/time-off/requests`, { credentials: 'include' });
      if (res.ok) setTimeOffRequests(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchNotificationCounts = useCallback(async () => {
    try {
      // Fetch late notifications count
      const notifRes = await fetch(`${API_URL}/api/employer/notifications`, { credentials: 'include' });
      if (notifRes.ok) {
        const notifications = await notifRes.json();
        setLateNotificationsCount(notifications.filter(n => n.type === "late_checkin").length);
      }
      // Fetch pending shift requests count
      const shiftsRes = await fetch(`${API_URL}/api/shifts/requests`, { credentials: 'include' });
      if (shiftsRes.ok) {
        const shifts = await shiftsRes.json();
        setPendingShiftRequestsCount(shifts.filter(s => s.status === "pending").length);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchEmployees();
    fetchTimeOffRequests();
    fetchNotificationCounts();
    fetchUnassignedLocationEmployees();

  const interval = setInterval(() => {
    fetchEmployees();
    fetchTimeOffRequests();
    fetchNotificationCounts();
    fetchUnassignedLocationEmployees();
  }, 30000);

  return () => clearInterval(interval);
}, [fetchEmployees, fetchTimeOffRequests, fetchNotificationCounts, fetchUnassignedLocationEmployees]);

  const fetchEmployeeHistory = async (emp, date) => {
    try {
      const res = await fetch(`${API_URL}/api/employees/${emp.user_id}/history?date=${format(date, 'yyyy-MM-dd')}`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setEmployeeHistory(data.records || []); }
    } catch (e) { console.error(e); }
  };

  const fetchAnalytics = async (emp, period) => {
    try {
      const res = await fetch(`${API_URL}/api/employees/${emp.user_id}/analytics?period=${period}`, { credentials: 'include' });
      if (res.ok) setAnalyticsData(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleViewHistory = (emp) => { setSelectedEmployee(emp); setSelectedDate(new Date()); fetchEmployeeHistory(emp, new Date()); setShowHistoryDialog(true); };
  const handleViewAnalytics = (emp) => { setSelectedEmployee(emp); setAnalyticsPeriod("month"); fetchAnalytics(emp, "month"); setShowAnalyticsDialog(true); };
  const handleDateChange = (date) => { if (date && selectedEmployee) { setSelectedDate(date); fetchEmployeeHistory(selectedEmployee, date); } };
  const handlePeriodChange = (period) => { setAnalyticsPeriod(period); if (selectedEmployee) fetchAnalytics(selectedEmployee, period); };

  const handleUpdateTimeOff = async (requestId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/time-off/${requestId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ status })
      });
      if (res.ok) { toast.success(`Request ${status}`); fetchTimeOffRequests(); }
    } catch (e) { toast.error("Failed"); }
  };

  const handleRemoveEmployee = async () => {
    if (!employeeToRemove) return;
    try {
      const res = await fetch(`${API_URL}/api/employees/${employeeToRemove.user_id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success(t("employeeRemoved")); setEmployeeToRemove(null); fetchEmployees(); }
    } catch (e) { toast.error("Failed"); }
  };

  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/company`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ name: companyName, pay_type: companyPayType, shifts_enabled: shiftsEnabled, timezone_offset: companyTimezone })
      });
      if (res.ok) { 
        toast.success(t("settingsSaved")); 
        setShowSettingsDialog(false); 
        setCompany(await res.json());
        // Refetch pending schedules if switching to salary
        if (companyPayType === "salary") {
          const pendingRes = await fetch(`${API_URL}/api/employer/pending-schedules`, { credentials: 'include' });
          if (pendingRes.ok) setPendingScheduleEmployees((await pendingRes.json()).pending_employees || []);
        } else {
          setPendingScheduleEmployees([]);
        }
      }
    } catch (e) { toast.error("Failed"); }
  };

  const handleSetPermanentSchedule = async () => {
    if (!scheduleEmployee) return;
    try {
      const res = await fetch(`${API_URL}/api/schedule/permanent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: scheduleEmployee.user_id, schedule: weeklySchedule })
      });
      if (res.ok) {
        toast.success(t("scheduleSet") || "Schedule set successfully");
        setShowScheduleDialog(false);
        setScheduleEmployee(null);
        // Refresh pending schedules
        const pendingRes = await fetch(`${API_URL}/api/employer/pending-schedules`, { credentials: 'include' });
        if (pendingRes.ok) setPendingScheduleEmployees((await pendingRes.json()).pending_employees || []);
        fetchEmployees();
      } else {
        toast.error("Failed to set schedule");
      }
    } catch (e) { toast.error("Error setting schedule"); }
  };

  const handleExportSchedule = (period, exportFormat) => {
    window.open(`${API_URL}/api/export/schedule?period=${period}&format=${exportFormat}`, '_blank');
    setShowScheduleExportDialog(false);
  };

  const handleClearAllTimeOff = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employer/time-off/clear`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success("Time off requests cleared and archived"); fetchTimeOffRequests(); }
    } catch (e) { toast.error("Failed to clear"); }
  };

  const handleClearAllShifts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/employer/shifts/clear`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success("Shift requests cleared and archived"); }
    } catch (e) { toast.error("Failed to clear"); }
  };

  const handleDeleteCompany = async () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/company`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        toast.success("Company deleted successfully");
        navigate('/');
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to delete company");
      }
    } catch (e) { toast.error("Failed to delete company"); }
  };

  const handleLogout = async () => {
    try { await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' }); }
    catch (e) { /* silent */ }
    navigate('/');
  };

  const copyInviteCode = async () => {
    if (company?.invite_code) {
      try {
        await navigator.clipboard.writeText(company.invite_code);
        toast.success(t("copiedToClipboard"));
      } catch (err) {
        // Fallback for environments where clipboard API is blocked
        const textArea = document.createElement('textarea');
        textArea.value = company.invite_code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          toast.success(t("copiedToClipboard"));
        } catch (e) {
          toast.error(t("copyFailed") || "Copy failed - please copy manually: " + company.invite_code);
        }
        document.body.removeChild(textArea);
      }
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscription/cancel`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        toast.success(t("subscriptionCancelledSuccess") || "Your subscription has been cancelled");
        setShowCancelSubscriptionDialog(false);
        // Refresh subscription status
        const statusRes = await fetch(`${API_URL}/api/subscription/status`, { credentials: 'include' });
        if (statusRes.ok) setSubscriptionStatus(await statusRes.json());
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to cancel subscription");
      }
    } catch (e) { toast.error("Failed to cancel subscription"); }
  };

  const formatTime = (ts) => ts ? format(new Date(ts), 'h:mm a') : '-';
  const checkedInCount = employees.filter(e => e.is_checked_in).length;
  const lateCount = employees.filter(e => e.is_late).length;
  const pendingTimeOff = timeOffRequests.filter(r => r.status === 'pending').length;

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'rtl' : 'ltr'}`}>
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <MapPin className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl text-foreground">{t("appName")}</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button variant="ghost" size="sm" onClick={() => setShowSettingsDialog(true)}><Settings className="w-4 h-4" /></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline text-sm font-medium">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="text-muted-foreground">{user?.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                {(user?.is_admin || user?.email === "ebrahemgouyrr@gmail.com") && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="text-destructive"><Shield className="w-4 h-4 mr-2" />Admin Dashboard</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/subscription')}><CreditCard className="w-4 h-4 mr-2" />Subscription</DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />{t("signOut")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Trial Banner - Always visible during trial (not dismissible) */}
        {subscriptionStatus?.status === "trial" && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="font-bold text-yellow-800 text-lg">{t("freeTrialActive") || "Free Trial Active"}</p>
                <p className="text-yellow-700">{subscriptionStatus.days_left} {t("days") || "days"}{subscriptionStatus.hours_left > 0 ? `, ${subscriptionStatus.hours_left} ${t("hours") || "hours"}` : ''} {t("remaining") || "remaining"}</p>
                <p className="text-sm text-yellow-600 mt-1">{subscriptionStatus.employee_count || 0} / {subscriptionStatus.max_employees || 5} {t("employees") || "employees"}</p>
              </div>
            </div>
            <Button onClick={() => navigate('/subscription')} className="bg-yellow-600 hover:bg-yellow-700 text-white">
              {t("upgradeNow") || "Upgrade Now"}
            </Button>
          </div>
        )}
        {subscriptionStatus?.status === "active" && !bannerDismissed && (
          <div className="mb-6 p-3 rounded-xl bg-green-50 border border-green-200 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <p className="text-green-800 font-medium">{t("activeSubscription") || "Active Subscription"}: {subscriptionStatus.plan?.charAt(0).toUpperCase() + subscriptionStatus.plan?.slice(1)} {t("plan") || "Plan"}</p>
            </div>
            <button onClick={dismissBanner} className="text-green-600 hover:text-green-800 p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        {company?.geofencing_enabled && unassignedLocationEmployees.length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">
                    Employees need geofencing assignment
                  </p>
                  <p className="text-sm text-amber-700">
                    {unassignedLocationEmployees.length} employee(s) cannot check in until a work location is assigned.
                  </p>
                </div>
              </div>

              <Button
                variant="outline"
                className="border-amber-300 bg-white"
                onClick={() => navigate("/employer/geofencing", { state: { user } })}
              >
                Customize Geofencing
              </Button>
            </div>
          </div>
        )}
        
        {/* Pending Schedules Banner (Salary Companies) */}
        {companyPayType === "salary" && pendingScheduleEmployees.length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-3 mb-3">
              <CalendarDays className="w-5 h-5 text-blue-600" />
              <p className="text-blue-800 font-medium">{t("pendingSchedules") || "Employees Need Schedules"}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {pendingScheduleEmployees.map(emp => (
                <Button 
                  key={emp.user_id} 
                  variant="outline" 
                  size="sm" 
                  className="bg-white"
                  onClick={() => { setScheduleEmployee(emp); setShowScheduleDialog(true); }}
                >
                  <Clock className="w-4 h-4 mr-2" />
                  {emp.display_name || emp.name}
                </Button>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-foreground">{company?.name || 'Your Company'}</h1>
            <p className="text-muted-foreground mt-1">{t("realTimeAttendance")}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button variant="outline" onClick={() => setShowScheduleExportDialog(true)} className="h-10">
              <Download className="w-4 h-4 mr-2" />{t("exportSchedule") || "Export Schedule"}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/employer/geofencing", { state: { user } })}
              className="h-10"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Geofencing
            </Button>
            <Button variant="outline" onClick={() => setShowPayrollDialog(true)} data-testid="export-payroll-btn" className="h-10">
              <FileSpreadsheet className="w-4 h-4 mr-2" />{t("exportPayroll")}
            </Button>
            {company?.invite_code && (
              <Card className="bg-accent/30 border-accent">
                <CardContent className="p-3 flex items-center gap-3">
                  <div><p className="text-xs text-muted-foreground">{t("inviteCode")}</p><p className="font-mono font-bold">{company.invite_code}</p></div>
                  <Button variant="ghost" size="sm" onClick={copyInviteCode}><Copy className="w-4 h-4" /></Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

       <Tabs defaultValue="employees" className="space-y-6">
  <TabsList
  className="
    flex
    w-full
    gap-2
    pb-2
    overflow-x-auto
    whitespace-nowrap
    flex-nowrap
  "
  style={{ WebkitOverflowScrolling: "touch" }}
>
    <TabsTrigger
      value="employees"
      className="flex-shrink-0 whitespace-nowrap"
    >
      <Users className="w-4 h-4 mr-1" />
      {t("employee")}
    </TabsTrigger>

    <TabsTrigger
      value="schedule"
      className="flex-shrink-0 whitespace-nowrap"
    >
      <CalendarDays className="w-4 h-4 mr-1" />
      {t("groupSchedule")}
    </TabsTrigger>

    <TabsTrigger
      value="shifts"
      className="flex-shrink-0 whitespace-nowrap"
    >
      {t("shiftRequests")}
      {pendingShiftRequestsCount > 0 && (
        <Badge className="ml-2 bg-orange-500">
          {pendingShiftRequestsCount}
        </Badge>
      )}
    </TabsTrigger>

    <TabsTrigger
      value="timeoff"
      className="flex-shrink-0 whitespace-nowrap"
    >
      {t("timeOffRequests")}
      {pendingTimeOff > 0 && (
        <Badge className="ml-2 bg-destructive">
          {pendingTimeOff}
        </Badge>
      )}
    </TabsTrigger>

    <TabsTrigger
      value="notifications"
      className="flex-shrink-0 whitespace-nowrap"
    >
      <Bell className="w-4 h-4 mr-1" />
      {t("attendanceAlerts") || "Attendance Alerts"}
      {lateNotificationsCount > 0 && (
        <Badge className="ml-2 bg-yellow-500">
          {lateNotificationsCount}
        </Badge>
      )}
    </TabsTrigger>

    <TabsTrigger
      value="analytics"
      className="flex-shrink-0 whitespace-nowrap"
    >
      <BarChart3 className="w-4 h-4 mr-1" />
      {t("totalAnalytics") || "Analytics"}
    </TabsTrigger>

    <TabsTrigger
      value="security"
      className="flex-shrink-0 whitespace-nowrap"
    >
      <Shield className="w-4 h-4 mr-1 text-destructive" />
      {t("securityAlerts")}
    </TabsTrigger>

    <TabsTrigger
      value="audit"
      className="flex-shrink-0 whitespace-nowrap"
    >
      {t("auditHistory") || "Audit History"}
    </TabsTrigger>

  </TabsList>

          {/* Combined Employees Tab */}
          <TabsContent value="employees">
            <div className="space-y-4">
              {/* View Toggle */}
              <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                <span className="text-sm font-medium">{t("viewLiveStatus") || "Live Status"}</span>
                <Switch checked={employeeViewMode === "all"} onCheckedChange={(checked) => setEmployeeViewMode(checked ? "all" : "live")} />
                <span className="text-sm font-medium">{t("viewAllEmployees") || "All Employees"}</span>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><Users className="w-5 h-5 text-primary" /></div><div><p className="text-2xl font-bold">{employees.length}</p><p className="text-xs text-muted-foreground">{t("totalEmployees")}</p></div></div></CardContent></Card>
                <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-primary" /></div><div><p className="text-2xl font-bold">{checkedInCount}</p><p className="text-xs text-muted-foreground">{t("currentlyWorkingNow") || "Currently Working (Now)"}</p></div></div></CardContent></Card>
                <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center"><XCircle className="w-5 h-5 text-muted-foreground" /></div><div><p className="text-2xl font-bold">{employees.length - checkedInCount}</p><p className="text-xs text-muted-foreground">{t("notCheckedInCount")}</p></div></div></CardContent></Card>
                <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-destructive" /></div><div><p className="text-2xl font-bold">{lateCount}</p><p className="text-xs text-muted-foreground">{t("lateToday") || "Late Today"}</p></div></div></CardContent></Card>
              </div>
              <p className="text-xs text-muted-foreground text-center">{t("liveStatusHelper") || "Live status updates automatically every 30 seconds"}</p>

              {/* Employee List/Table */}
              <Card>
                <CardHeader>
                  <div>
                    <CardTitle>{employeeViewMode === "live" ? t("employeeStatus") : t("allEmployees")}</CardTitle>
                    <CardDescription>{employeeViewMode === "live" ? t("realTimeStatus") : t("manageEmployees")}</CardDescription>
                  </div>
                </CardHeader>
                <CardContent>
                  {employees.length === 0 ? (
                    <div className="text-center py-12"><Users className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" /><p className="text-muted-foreground">{t("noEmployeesYet")}</p></div>
                  ) : employeeViewMode === "live" ? (
                    <div className="overflow-x-auto">             
                      <Table>
                      <TableHeader><TableRow><TableHead>{t("employee")}</TableHead><TableHead>{t("status")}</TableHead><TableHead className="hidden md:table-cell">{t("lastCheckIn")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {employees.map((emp) => (
                          <TableRow key={emp.user_id} className="hover:bg-muted/50 transition-colors cursor-default">
                            <TableCell><div className="flex items-center gap-3"><Avatar className="w-10 h-10"><AvatarImage src={emp.picture} /><AvatarFallback className="bg-accent">{emp.name?.charAt(0)}</AvatarFallback></Avatar><div><p className="font-medium">{emp.name}</p>{emp.job_title && <p className="text-xs text-primary/80 font-medium">{emp.job_title}</p>}<p className="text-xs text-muted-foreground">{emp.email}</p></div></div></TableCell>
                            <TableCell>{emp.time_off_today ? <Badge variant="outline" className="bg-purple-100 text-purple-800"><CalendarOff className="w-3 h-3 mr-1" />{t("timeOff")}</Badge> : emp.is_checked_in ? <><Badge className="bg-accent text-accent-foreground"><CheckCircle2 className="w-3 h-3 mr-1" />{t("working")}</Badge>{emp.is_late && <Badge variant="destructive" className="ml-1">{t("late")}</Badge>}</> : <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" />{t("offline")}</Badge>}</TableCell>
                            <TableCell className="hidden md:table-cell font-mono text-sm text-muted-foreground">{emp.last_check_in ? formatTime(emp.last_check_in.timestamp) : '-'}</TableCell>
                            <TableCell className="text-right">
                              <TooltipProvider>
                                <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={() => handleViewHistory(emp)}><Eye className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent><p>{t("viewActivity") || "View activity"}</p></TooltipContent></Tooltip>
                                <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={() => handleViewAnalytics(emp)}><BarChart3 className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent><p>{t("viewAnalytics") || "View analytics"}</p></TooltipContent></Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {employees.map((emp) => (
                        <div key={emp.user_id} className="flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted/70 transition-colors">
                          <div className="flex items-center gap-3"><Avatar className="w-12 h-12"><AvatarImage src={emp.picture} /><AvatarFallback className="bg-accent">{emp.name?.charAt(0)}</AvatarFallback></Avatar><div><p className="font-medium">{emp.name}</p>{emp.job_title && <p className="text-sm text-primary/80 font-medium">{emp.job_title}</p>}<p className="text-sm text-muted-foreground">{emp.email}</p></div></div>
                          <div className="flex items-center gap-2">
                            <TooltipProvider>
                              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={() => handleViewAnalytics(emp)}><BarChart3 className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent><p>{t("viewAnalytics") || "View analytics"}</p></TooltipContent></Tooltip>
                              <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" className="text-destructive" onClick={() => setEmployeeToRemove(emp)}><Trash2 className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent><p>{t("removeEmployee") || "Remove employee"}</p></TooltipContent></Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="schedule">
            <GroupScheduleTab employees={employees} />
          </TabsContent>

          <TabsContent value="shifts">
            <ShiftsTab onClearAll={handleClearAllShifts} />
          </TabsContent>

          <TabsContent value="timeoff">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t("timeOffRequests")} {timeOffRequests.length > 0 && <span className="text-muted-foreground font-normal text-base">({timeOffRequests.length} total, {pendingTimeOff} pending)</span>}</CardTitle>
                  <CardDescription>{t("timeOffRequestsDesc") || "Review and approve employee time-off requests"}</CardDescription>
                </div>
                {timeOffRequests.length > 0 && <Button variant="outline" size="sm" onClick={handleClearAllTimeOff} className="text-muted-foreground hover:text-destructive">Clear All</Button>}
              </CardHeader>
              <CardContent>
                {timeOffRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarOff className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground font-medium">{t("noTimeOffRequests")}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t("timeOffRequestsEmptyHelper") || "Time-off requests from employees will appear here"}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {timeOffRequests.map((req) => (
                      <div key={req.request_id} className="p-4 rounded-xl bg-muted/50 hover:bg-muted/70 transition-colors">
                        <div className="flex items-start justify-between">
                          <div><div className="flex items-center gap-2 mb-1"><span className="font-medium">{req.employee_name}</span><Badge className={req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : req.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{t(req.status)}</Badge></div><p className="text-sm text-muted-foreground">{req.date} - {t(`reasons.${req.reason_type}`)}</p>{req.reason_details && <p className="text-sm text-muted-foreground mt-1 italic">&quot;{req.reason_details}&quot;</p>}</div>
                          {req.status === 'pending' && <div className="flex gap-2"><Button size="sm" variant="outline" className="text-green-600" onClick={() => handleUpdateTimeOff(req.request_id, 'approved')}>{t("approve")}</Button><Button size="sm" variant="outline" className="text-red-600" onClick={() => handleUpdateTimeOff(req.request_id, 'denied')}>{t("deny")}</Button></div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <TotalAnalyticsTab />
          </TabsContent>

          <TabsContent value="notifications">
            <NotificationsTab />
          </TabsContent>

          <TabsContent value="security">
            <SecurityTab />
          </TabsContent>

          <TabsContent value="audit">
            <AuditHistoryTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialogs */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-3"><Avatar className="w-10 h-10"><AvatarImage src={selectedEmployee?.picture} /><AvatarFallback>{selectedEmployee?.name?.charAt(0)}</AvatarFallback></Avatar>{selectedEmployee?.name} - {t("history")}</DialogTitle></DialogHeader>
          <div className="flex justify-end mb-4"><Popover><PopoverTrigger asChild><Button variant="outline" size="sm"><CalendarIcon className="w-4 h-4 mr-2" />{format(selectedDate, 'MMM d, yyyy')}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={selectedDate} onSelect={handleDateChange} /></PopoverContent></Popover></div>
          {employeeHistory.length === 0 ? <div className="text-center py-8"><Clock className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">{t("noActivity")}</p></div> : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {employeeHistory.map((record, i) => (
                <div key={record.check_id || i} className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${record.type === 'check_in' ? 'bg-accent' : 'bg-secondary'}`}>{record.type === 'check_in' ? <CheckCircle2 className="w-5 h-5 text-primary" /> : <XCircle className="w-5 h-5 text-muted-foreground" />}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{record.type === 'check_in' ? t("checkedIn") : t("checkedOut")}</p>
                      {record.is_late && <Badge variant="destructive">{t("late")}</Badge>}
                      {record.flag_reason && <Badge variant="outline" className="text-orange-600">{record.flag_reason === 'ip' ? t("ipFlag") || 'IP Flag' : record.flag_reason}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground font-mono">{record.latitude?.toFixed(6)}, {record.longitude?.toFixed(6)}</p>
                    {record.late_reason && <p className="text-sm text-muted-foreground mt-1">{t("reason") || "Reason"}: {record.late_reason}</p>}
                  </div>
                  <span className="text-sm font-mono text-muted-foreground">{formatTime(record.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showAnalyticsDialog} onOpenChange={setShowAnalyticsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="flex items-center gap-3"><BarChart3 className="w-5 h-5" />{selectedEmployee?.name} - {t("analytics")}</DialogTitle></DialogHeader>
          <div className="flex justify-end mb-4"><Select value={analyticsPeriod} onValueChange={handlePeriodChange}><SelectTrigger className="w-32"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="week">{t("week")}</SelectItem><SelectItem value="month">{t("month")}</SelectItem><SelectItem value="year">{t("year")}</SelectItem></SelectContent></Select></div>
          {analyticsData && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-primary">{analyticsData.summary.total_days_worked}</p><p className="text-sm text-muted-foreground">{t("totalDaysWorked")}</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-green-600">{analyticsData.summary.on_time_days}</p><p className="text-sm text-muted-foreground">{t("onTimeDays")}</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-destructive">{analyticsData.summary.late_days}</p><p className="text-sm text-muted-foreground">{t("lateDays")}</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold">{analyticsData.summary.total_hours}h</p><p className="text-sm text-muted-foreground">{t("totalHours")}</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold">{analyticsData.summary.avg_hours_per_day}h</p><p className="text-sm text-muted-foreground">{t("avgHoursPerDay")}</p></CardContent></Card>
              <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-primary">{analyticsData.summary.attendance_rate}%</p><p className="text-sm text-muted-foreground">{t("attendanceRate")}</p></CardContent></Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t("companySettings")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            {/* Admin Dashboard Link - Only visible to admins */}
            {user?.is_admin && (
              <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-purple-800">{t("adminAccess") || "Admin Access"}</p>
                      <p className="text-xs text-purple-600">{t("manageAllUsersCompanies") || "Manage all users, companies, and pricing"}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                    onClick={() => { setShowSettingsDialog(false); navigate('/admin'); }}
                    data-testid="admin-dashboard-link"
                  >
                    {t("openAdminDashboard") || "Admin Dashboard"}
                  </Button>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{t("companyName") || "Company Name"}</Label>
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Enter company name" />
            </div>
            <div className="space-y-2">
              <Label>{t("payType") || "Pay Type"}</Label>
              <Select value={companyPayType} onValueChange={setCompanyPayType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select pay type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">{t("hourly") || "Hourly"} - {t("hourlyDesc") || "Flexible shifts"}</SelectItem>
                  <SelectItem value="salary">{t("salary") || "Salary"} - {t("salaryDesc") || "Fixed schedules"}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{companyPayType === "salary" ? (t("salaryPayTypeHelp") || "Set permanent weekly schedules for employees") : (t("hourlyPayTypeHelp") || "Employees request shifts or are assigned shifts")}</p>
            </div>
            <div className="space-y-2">
              <Label>{t("companyTimezone") || "Company Timezone"}</Label>
              <Select value={String(companyTimezone)} onValueChange={(v) => setCompanyTimezone(Number(v))}>
                <SelectTrigger data-testid="timezone-select">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONE_OPTIONS.map(tz => (
                    <SelectItem key={tz.value} value={String(tz.value)}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t("timezoneHelp") || "Used for late check-in detection and schedule times"}</p>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div>
                <p className="font-medium">{t("shiftsEnabled") || "Shift Scheduling"}</p>
                <p className="text-sm text-muted-foreground">{companyPayType === "salary" ? "Allow schedule adjustment requests" : "Allow employees to request shifts"}</p>
              </div>
              <Switch checked={shiftsEnabled} onCheckedChange={setShiftsEnabled} />
            </div>
            
            {/* Subscription Management */}
            {subscriptionStatus?.status === "active" && (
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium mb-3">{t("manageSubscription") || "Manage Subscription"}</p>
                <div className="p-3 rounded-lg bg-muted/50 mb-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("currentPlan") || "Current Plan"}</span>
                    <span className="font-medium capitalize">{subscriptionStatus.plan}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("subscriptionStatus") || "Status"}</span>
                    <Badge variant="outline" className="bg-green-100 text-green-800">{t("active") || "Active"}</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("billingCycle") || "Billing Cycle"}</span>
                    <span className="font-medium">{t("monthlyBilling") || "Monthly"}</span>
                  </div>
                  {subscriptionStatus.is_recurring && (
                    <div className="flex items-center gap-2 text-sm pt-2 border-t">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-green-700">{t("subscriptionRenewsAutomatically") || "Your subscription renews automatically each month"}</span>
                    </div>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => { setShowSettingsDialog(false); setShowCancelSubscriptionDialog(true); }}
                >
                  {t("cancelSubscription") || "Cancel Subscription"}
                </Button>
              </div>
            )}
            
            {/* Multi-Employer Invite Section */}
            {company?.allow_multiple_employers && (
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {t("multiEmployerInvite") || "Invite Another Employer"}
                </p>
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-3">
                  <p className="text-sm text-blue-700">
                    {t("multiEmployerInviteDesc") || "Share this link with another person to invite them as an employer for your company. They will have full employer access."}
                  </p>
                  <div className="flex items-center gap-2">
                    <Input 
                      readOnly 
                      value={`${window.location.origin}/auth?employer_invite=${company?.invite_code}`}
                      className="text-xs bg-white"
                      data-testid="employer-invite-link"
                    />
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const link = `${window.location.origin}/auth?employer_invite=${company?.invite_code}`;
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard.writeText(link).then(() => {
                            toast.success(t("employerInviteLinkCopied") || "Employer invite link copied!");
                          }).catch(() => {
                            toast.info("Link: " + link);
                          });
                        } else {
                          // Fallback for environments without clipboard API
                          toast.info("Link: " + link);
                        }
                      }}
                      data-testid="copy-employer-invite-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600">
                    {t("multiEmployerInviteNote") || "Note: The invited person must select 'I'm an Employer' and enter this invite code during registration."}
                  </p>
                </div>
              </div>
            )}
            
            {/* Danger Zone */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-destructive mb-3">{t("dangerZone") || "Danger Zone"}</p>
              <Button 
                variant="outline" 
                className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={() => { setShowSettingsDialog(false); setShowDeleteCompanyDialog(true); }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t("deleteCompany") || "Delete Company"}
              </Button>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSettingsDialog(false)}>{t("cancel")}</Button><Button onClick={handleSaveSettings}>{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={showCancelSubscriptionDialog} onOpenChange={setShowCancelSubscriptionDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cancelSubscriptionTitle") || "Cancel Subscription?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("cancelSubscriptionWarning") || "Your subscription will be cancelled at the end of the current billing period. You will lose access to all premium features."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelSubscription} className="bg-destructive text-destructive-foreground">
              {t("cancelSubscription") || "Cancel Subscription"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showScheduleExportDialog} onOpenChange={setShowScheduleExportDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("exportSchedule") || "Export Schedule"}</DialogTitle><DialogDescription>Download weekly or monthly schedule</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <p className="font-medium text-sm">{t("weeklyExport") || "Weekly"}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleExportSchedule('week', 'csv')}>CSV</Button>
                <Button size="sm" onClick={() => handleExportSchedule('week', 'pdf')}>PDF</Button>
              </div>
            </div>
            <div className="space-y-2">
              <p className="font-medium text-sm">{t("monthlyExport") || "Monthly"}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleExportSchedule('month', 'csv')}>CSV</Button>
                <Button size="sm" onClick={() => handleExportSchedule('month', 'pdf')}>PDF</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!employeeToRemove} onOpenChange={() => setEmployeeToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t("removeEmployee")}</AlertDialogTitle><AlertDialogDescription>{t("confirmRemove")} {employeeToRemove?.name}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>{t("cancel")}</AlertDialogCancel><AlertDialogAction onClick={handleRemoveEmployee} className="bg-destructive text-destructive-foreground">{t("remove")}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PayrollDialog open={showPayrollDialog} onOpenChange={setShowPayrollDialog} />
      
      {/* Set Permanent Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("setSchedule") || "Set Weekly Schedule"}</DialogTitle>
            <DialogDescription>
              {scheduleEmployee ? `${t("settingScheduleFor") || "Setting schedule for"} ${scheduleEmployee.display_name || scheduleEmployee.name}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4 max-h-96 overflow-y-auto">
            {DAY_NAMES.map((day, idx) => {
              const daySchedule = weeklySchedule.find(s => s.day_of_week === idx) || { day_of_week: idx, start_time: "09:00", end_time: "17:00", is_working_day: true };
              return (
                <div key={idx} className={`p-3 rounded-lg border ${daySchedule.is_working_day ? 'bg-background' : 'bg-muted/30'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{t(day.toLowerCase()) || day}</span>
                    <Switch 
                      checked={daySchedule.is_working_day} 
                      onCheckedChange={(checked) => {
                        setWeeklySchedule(prev => prev.map(s => s.day_of_week === idx ? {...s, is_working_day: checked} : s));
                      }}
                    />
                  </div>
                  {daySchedule.is_working_day && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{t("startTime") || "Start"}</Label>
                        <Input 
                          type="time" 
                          value={daySchedule.start_time}
                          onChange={(e) => {
                            setWeeklySchedule(prev => prev.map(s => s.day_of_week === idx ? {...s, start_time: e.target.value} : s));
                          }}
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t("endTime") || "End"}</Label>
                        <Input 
                          type="time" 
                          value={daySchedule.end_time}
                          onChange={(e) => {
                            setWeeklySchedule(prev => prev.map(s => s.day_of_week === idx ? {...s, end_time: e.target.value} : s));
                          }}
                          className="h-9"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>{t("cancel")}</Button>
            <Button onClick={handleSetPermanentSchedule}>{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Company - First Confirmation */}
      <AlertDialog open={showDeleteCompanyDialog} onOpenChange={setShowDeleteCompanyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              {t("deleteCompanyTitle") || "Delete Company?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteCompanyWarning") || "This action will permanently delete your company and all associated data including employees, shifts, time-off requests, and analytics. All employees will be removed from the company."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => { setShowDeleteCompanyDialog(false); setShowConfirmDeleteCompany(true); }}
              className="bg-destructive text-destructive-foreground"
            >
              {t("continue") || "Continue"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Company - Second Confirmation with text input */}
      <AlertDialog open={showConfirmDeleteCompany} onOpenChange={(open) => { setShowConfirmDeleteCompany(open); if (!open) setDeleteConfirmText(""); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              {t("finalConfirmation") || "Final Confirmation Required"}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>{t("deleteCompanyFinalWarning") || "You are about to permanently delete your company. This action CANNOT be undone."}</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>{t("allEmployeesRemoved") || "All employees will be removed from the company"}</li>
                <li>{t("allDataDeleted") || "All shifts, time-off requests, and analytics will be deleted"}</li>
                <li>{t("subscriptionCancelled") || "Your subscription will be cancelled"}</li>
              </ul>
              <div className="pt-2">
                <Label htmlFor="confirm-delete-company">{t("typeDelete") || "Type"} <strong>DELETE</strong> {t("toConfirm") || "to confirm"}:</Label>
                <Input 
                  id="confirm-delete-company"
                  value={deleteConfirmText} 
                  onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                  placeholder="Type DELETE here"
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>{t("cancel") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteCompany}
              disabled={deleteConfirmText !== "DELETE"}
              className="bg-destructive text-destructive-foreground disabled:opacity-50"
            >
              {t("deleteCompany") || "Delete Company"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmployerDashboard;
